import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ReceiptText, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './SalesTaxCalculatorPage/components/CalculationForm';
import ResultsOverview from './SalesTaxCalculatorPage/components/ResultsOverview';
import TaxDetails from './SalesTaxCalculatorPage/components/TaxDetails';
import QuickCalculationSidebar from './SalesTaxCalculatorPage/components/QuickCalculationSidebar';
import SavedCalculationsSidebar from './SalesTaxCalculatorPage/components/SavedCalculationsSidebar';
import TaxRatesSidebar from './SalesTaxCalculatorPage/components/TaxRatesSidebar';
import InfoContent from './SalesTaxCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  amount: '10000',
  taxRate: '20', // НДС ставка в процентах
  calculationType: 'add', // 'add' (начисление) или 'extract' (выделение)
  includeOtherTaxes: false,
  otherTaxRate: '0', // Дополнительный налог (например, для будущих расширений)
  otherTaxName: '',
  items: [], // Для расчета по нескольким позициям
  regionCode: '', // Код региона РФ
  useDefaultRates: true,
  showInvoiceDetails: false, // Показать подробный расчет для счет-фактуры
  accountingType: 'general', // Тип учета: общий, УСН и т.д.
  dateOfOperation: new Date().toISOString().split('T')[0],
};

// Определение доступных ставок НДС
export const vatRates = [
  { id: '20', name: '20%', description: 'Основная ставка НДС' },
  { id: '10', name: '10%', description: 'Пониженная ставка (продукты питания, детские товары и т.д.)' },
  { id: '0', name: '0%', description: 'Нулевая ставка (экспорт и т.д.)' },
  { id: 'exempt', name: 'Без НДС', description: 'Освобождение от НДС по ст. 149 НК РФ' },
  { id: '20/120', name: '20/120', description: 'Расчетная ставка (выделение НДС из суммы с налогом)' },
  { id: '10/110', name: '10/110', description: 'Расчетная ставка (выделение НДС из суммы с налогом)' },
];

// Типы учета
export const accountingTypes = [
  { id: 'general', name: 'Общая система', description: 'Стандартный учет с НДС' },
  { id: 'simplified', name: 'УСН', description: 'Упрощенная система налогообложения' },
  { id: 'envd', name: 'ЕНВД', description: 'Единый налог на вмененный доход (до 2021 г.)' },
  { id: 'patent', name: 'ПСН', description: 'Патентная система налогообложения' },
  { id: 'ausn', name: 'АУСН', description: 'Автоматизированная УСН (экспериментальный режим)' },
];

/**
 * SalesTaxCalculatorPage Component - Полный калькулятор НДС с различными налоговыми ставками и дополнительными параметрами.
 */
const SalesTaxCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('vat-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор НДС | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор НДС (налога на добавленную стоимость) онлайн: расчет суммы с НДС и без НДС, выделение НДС из суммы, расчет по разным ставкам, формирование счетов-фактур и другие операции с НДС в России.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных вычислений из localStorage ---
  useEffect(() => {
    const savedCalculationsData = localStorage.getItem('savedVatCalculations');
    if (savedCalculationsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalculationsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'add-vat':
          newState.calculationType = 'add';
          break;
        case 'exclude-vat':
          newState.calculationType = 'extract';
          break;
        case 'different-rates':
          newState.showInvoiceDetails = true;
          newState.items = [
            { id: 1, description: 'Товар 1', amount: 10000, taxRate: '20' },
            { id: 2, description: 'Товар 2', amount: 5000, taxRate: '10' }
          ];
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setCalculationHistory([]);
    setErrors({});
    setShowDetails(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Расчет НДС от ${formData.amount} ₽`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      calculationType: formData.calculationType
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedVatCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedVatCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
    }
  };

  // Добавление расчета в историю
  const addToHistory = (calculation) => {
    setCalculationHistory(prev => [calculation, ...prev].slice(0, 10));
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <ReceiptText className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор НДС'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              vatRates={vatRates}
              accountingTypes={accountingTypes}
              saveCalculation={saveCalculation}
              addToHistory={addToHistory}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                {showDetails && (
                  <TaxDetails 
                    results={results} 
                    formData={formData}
                    setShowDetails={setShowDetails}
                  />
                )}
                {!showDetails && results && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowDetails(true)}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 text-sm font-medium"
                    >
                      Показать подробный расчет
                    </button>
                  </div>
                )}
              </>
            )}

            {/* --- Информационный контент о НДС --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрого расчета --- */}
            <QuickCalculationSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              vatRates={vatRates}
            />

            {/* --- Панель сохраненных расчетов --- */}
            <SavedCalculationsSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
              calculationHistory={calculationHistory}
            />

            {/* --- Ставки НДС и полезная информация --- */}
            <TaxRatesSidebar vatRates={vatRates} />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Tax Rate Badges */
        .tax-rate-badge {
          @apply inline-flex items-center px-2 py-1 rounded-md text-xs font-medium;
        }
        .tax-rate-badge-20 {
          @apply bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300;
        }
        .tax-rate-badge-10 {
          @apply bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300;
        }
        .tax-rate-badge-0 {
          @apply bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300;
        }
        .tax-rate-badge-exempt {
          @apply bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300;
        }
        .tax-rate-badge-20\/120, .tax-rate-badge-10\/110 {
          @apply bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300;
        }
      `}</style>
    </div>
  );
};

export default SalesTaxCalculatorPage;